<template>
  <v-dialog :value="value" max-width="1000px" scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="headline grey lighten-2 py-3 px-4">
        <v-icon left color="primary">{{
          isStock ? "mdi-package-variant-closed" : "mdi-format-list-bulleted"
        }}</v-icon>
        {{ title || defaultTitle }}
        <v-spacer></v-spacer>
        <v-btn icon @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-4">
        <v-row class="mb-3 grey lighten-4 pa-2 rounded mx-0" dense>
          <v-col cols="12" sm="3">
            <div class="grey--text text--darken-1 caption text-uppercase">PO CRM</div>
            <div class="subtitle-1 font-weight-bold primary--text">
              {{ context.poNumber || (context.crmPoId ? "#" + context.crmPoId : "—") }}
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="grey--text text--darken-1 caption text-uppercase">Konteks</div>
            <div class="subtitle-1 font-weight-bold">{{ contextLabel }}</div>
          </v-col>
          <v-col cols="12" sm="3" v-if="isStock">
            <div class="grey--text text--darken-1 caption text-uppercase">
              Nilai jurnal MR (bruto)
            </div>
            <div class="subtitle-1 font-weight-bold">Rp {{ formatIdr(journalTotal) }}</div>
          </v-col>
          <v-col cols="12" :sm="isStock ? 3 : 6" class="text-right">
            <div class="grey--text text--darken-1 caption text-uppercase">
              {{ isStock ? "Stok neto ke PO ini" : bucketLabel + " ke PO ini" }}
            </div>
            <div class="subtitle-1 font-weight-bold">
              Rp {{ formatIdr(context.expectedAmount) }}
              <span class="grey--text">· {{ groups.length }} {{ groupNoun }}</span>
            </div>
          </v-col>
        </v-row>

        <div v-if="isStock" class="d-flex align-center mb-3">
          <span class="caption grey--text text--darken-1 mr-3">Kelompokkan:</span>
          <v-btn-toggle v-model="groupBy" mandatory dense color="primary" @change="load">
            <v-btn value="MR" small>Per MR</v-btn>
            <v-btn value="ITEM" small>Per item stok</v-btn>
          </v-btn-toggle>
        </div>

        <v-data-table
          :headers="groupHeaders"
          :items="groups"
          item-key="key"
          show-expand
          :expanded.sync="expanded"
          :loading="loading"
          hide-default-footer
          :items-per-page="-1"
          dense
          class="elevation-1"
        >
          <template v-slot:item.number="{ item }">
            <strong>{{ item.number }}</strong>
            <v-icon v-if="item.approved" small color="success" class="ml-1" title="approved"
              >mdi-check</v-icon
            >
            <v-icon
              v-else-if="!isStock && item.approved === false"
              small
              color="warning"
              class="ml-1"
              title="belum approve / belum ada PO"
              >mdi-clock-outline</v-icon
            >
            <v-icon
              v-if="item.fxMissing"
              small
              color="error"
              class="ml-1"
              title="kurs tidak ditemukan"
              >mdi-currency-usd-off</v-icon
            >
          </template>
          <template v-slot:item.name="{ item }">
            {{ item.name || "—" }}
            <div v-if="item.description" class="caption grey--text text--darken-1">{{ item.description }}</div>
          </template>
          <template v-slot:item.date="{ item }">{{ formatDate(item.date) }}</template>
          <template v-slot:item.rate="{ item }">{{ formatIdr(item.rate) }}</template>
          <template v-slot:item.journalAmountIdr="{ item }">{{
            formatIdr(item.journalAmountIdr)
          }}</template>
          <template v-slot:item.amountIdr="{ item }">
            <v-icon
              v-if="item.estimated"
              x-small
              color="warning"
              class="mr-1"
              title="alokasi estimasi"
              >mdi-approximately-equal</v-icon
            >{{ formatIdr(item.amountIdr) }}
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0">
              <v-simple-table dense class="grey lighten-5">
                <thead>
                  <tr>
                    <th>Part number</th>
                    <th>Nama</th>
                    <th class="text-right">Qty</th>
                    <th v-if="isStock" class="text-right">Qty MR</th>
                    <th class="text-right">Harga satuan</th>
                    <th>Curr</th>
                    <th class="text-right">Nilai IDR</th>
                    <th>{{ isStock ? "Panel / Alokasi" : "PR / Alokasi" }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="l in item.lines"
                    :key="l.id || l.prDetailId + '-' + l.posId + '-' + l.mrId + '-' + l.productId"
                    :class="{ 'grey--text': Number(l.qty) === 0 }"
                  >
                    <td>{{ l.partNum || "—" }}</td>
                    <td>
                      {{ l.partName || "—" }}
                      <div v-if="l.partDesc" class="caption grey--text text--darken-1">{{ l.partDesc }}</div>
                    </td>
                    <td class="text-right">{{ formatQty(l.qty) }}</td>
                    <td v-if="isStock" class="text-right">{{ formatQty(l.qtyGross) }}</td>
                    <td class="text-right">{{ formatIdr(l.unitPrice) }}</td>
                    <td>{{ l.currency }}</td>
                    <td class="text-right">{{ formatIdr(l.amountIdr) }}</td>
                    <td>
                      <span class="clickable-cell" v-if="l.prNumber">{{ l.prNumber }}</span>
                      <span v-if="isStock && l.panelCodeId" class="caption"
                        >panel #{{ l.panelCodeId }}</span
                      >
                      <v-chip
                        x-small
                        class="ml-1"
                        :color="methodColor(l.allocationMethod)"
                        text-color="white"
                        :title="methodLabel(l.allocationMethod)"
                      >
                        {{ l.allocationMethod
                        }}<span v-if="Number(l.allocationShare) < 1">
                          {{ Math.round(l.allocationShare * 100) }} %</span
                        >
                      </v-chip>
                      <v-chip
                        x-small
                        class="ml-1"
                        v-if="l.dedupSource && Number(l.qty) === 0"
                        color="grey lighten-2"
                        title="qty MR sudah dibeli lewat PO job ini"
                        >terdedup</v-chip
                      >
                      <v-chip
                        x-small
                        class="ml-1"
                        v-if="l.crossJobHint"
                        color="orange lighten-4"
                        :title="'PR dari ' + l.crossJobHint + ' yang tidak terhubung ke PO ini'"
                        >lintas job</v-chip
                      >
                      <v-chip
                        x-small
                        class="ml-1"
                        v-if="l.productUnvalidated"
                        color="red lighten-4"
                        title="PR detail belum divalidasi (product_id 0)"
                        >belum validasi</v-chip
                      >
                      <v-chip
                        x-small
                        class="ml-1"
                        v-if="
                          l.priceSource && l.priceSource !== 'PO' && l.priceSource !== 'SNAPSHOT'
                        "
                        color="blue-grey lighten-4"
                        :title="'sumber harga ' + l.priceSource"
                        >{{ l.priceSource }}</v-chip
                      >
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </td>
          </template>
        </v-data-table>

        <div class="d-flex align-center mt-3 caption grey--text text--darken-1 flex-wrap">
          <span v-if="!isStock">
            Freight termasuk: <strong>Rp {{ formatIdr(freightTotal) }}</strong> · Diskon header:
            <strong>Rp {{ formatIdr(discountTotal) }}</strong> · PPN tidak dihitung
          </span>
          <span v-else
            >Valuasi: <strong>snapshotPriceIdr</strong> · Dedup: qty MR − qty PO job per
            product_id</span
          >
          <v-spacer></v-spacer>
          <span>
            Σ = <strong>Rp {{ formatIdr(sum) }}</strong>
            <v-icon small :color="matches ? 'success' : 'error'" class="ml-1">{{
              matches ? "mdi-check" : "mdi-alert"
            }}</v-icon>
            <span :class="matches ? 'success--text' : 'error--text'">{{
              matches ? "sama dengan angka di list" : "berbeda dari angka di list"
            }}</span>
          </span>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="py-2 px-4">
        <span class="caption grey--text text--darken-1">{{
          isStock
            ? "Baris terdedup tetap tampil (qty neto 0) agar jelas dihitung di belanja PO"
            : "Klik baris PO supplier untuk melihat item"
        }}</span>
        <v-spacer></v-spacer>
        <v-btn
          color="success"
          text
          @click="exportExcel"
          :disabled="!context.crmPoId"
          :loading="exporting"
        >
          <v-icon left>mdi-file-excel</v-icon>Ekspor Excel
        </v-btn>
        <v-btn color="primary" text @click="$emit('input', false)">Tutup</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import fileDownload from "js-file-download";
import {
  amountsMatch,
  formatDate,
  formatIdr,
  formatQty,
  isEstimatedLine,
  methodColor,
  methodLabel,
  sumBy,
} from "@/utils/budgetMonitor";

export default {
  name: "budget-spend-breakdown",
  props: {
    value: Boolean,
    title: { type: String, default: "" },
    /** { crmPoId, jobId, bucket: 'SPEND'|'COMMITTED'|'STOCK', expectedAmount, poNumber, jobName } */
    context: { type: Object, required: true },
  },
  data: () => ({ groups: [], expanded: [], loading: false, exporting: false, groupBy: "MR" }),
  computed: {
    isStock() {
      return this.context.bucket === "STOCK";
    },
    bucketLabel() {
      return this.context.bucket === "STOCK"
        ? "Stok"
        : this.context.bucket === "COMMITTED"
        ? "Committed"
        : "Belanja";
    },
    groupNoun() {
      if (this.isStock) return this.groupBy === "ITEM" ? "item" : "MR";
      return "PO supplier";
    },
    defaultTitle() {
      return `${this.bucketLabel} · ${this.context.poNumber || "PO"}`;
    },
    contextLabel() {
      return `${this.bucketLabel} · ${
        this.context.jobName
          ? "job " + this.context.jobName
          : this.context.jobId
          ? "job #" + this.context.jobId
          : "semua job"
      }`;
    },
    sum() {
      return sumBy(this.groups, "amountIdr");
    },
    matches() {
      return amountsMatch(this.sum, this.context.expectedAmount);
    },
    allLines() {
      return this.groups.reduce((acc, g) => acc.concat(g.lines || []), []);
    },
    freightTotal() {
      return sumBy(this.allLines, "freightIdr");
    },
    discountTotal() {
      return sumBy(this.allLines, "discountIdr");
    },
    journalTotal() {
      return sumBy(this.groups, "journalAmountIdr");
    },
    groupHeaders() {
      const base = this.isStock
        ? [
            { text: this.groupBy === "MR" ? "MR" : "Part number", value: "number" },
            this.groupBy === "MR"
              ? { text: "Tgl pick", value: "date" }
              : { text: "Nama", value: "name" },
            { text: "Job", value: "job" },
            { text: "Item", value: "itemCount", align: "end" },
            { text: "Nilai jurnal MR", value: "journalAmountIdr", align: "end" },
            { text: "Neto ke PO ini (IDR)", value: "amountIdr", align: "end" },
          ]
        : [
            { text: "PO supplier", value: "number" },
            { text: "Supplier", value: "name" },
            { text: "Tgl PO", value: "date" },
            { text: "Curr", value: "currency" },
            { text: "Kurs", value: "rate", align: "end" },
            { text: "Job", value: "job" },
            { text: "Item", value: "itemCount", align: "end" },
            { text: "Nilai ke PO ini (IDR)", value: "amountIdr", align: "end" },
          ];
      return [{ text: "", value: "data-table-expand" }, ...base];
    },
  },
  watch: {
    value(v) {
      if (v) this.load();
    },
  },
  methods: {
    formatIdr,
    formatQty,
    formatDate,
    methodColor,
    methodLabel,
    async load() {
      this.loading = true;
      this.groups = [];
      try {
        const params = {
          bucket: this.context.bucket,
          groupBy: this.isStock ? this.groupBy : "POS",
        };
        let res;
        if (this.context.crmPoId) {
          res = await this.$store.dispatch("budgetMonitor/fetchPoLines", {
            crmPoId: this.context.crmPoId,
            jobId: this.context.jobId,
            ...params,
          });
        } else if (this.context.jobId !== null && this.context.jobId !== undefined) {
          res = await this.$store.dispatch("budgetMonitor/fetchJobLines", {
            jobId: this.context.jobId,
            ...params,
          });
        } else {
          res = await this.$store.dispatch("budgetMonitor/fetchNoJobLines", this.context.bucket);
        }
        this.groups = (res || []).map(g => ({
          ...g,
          estimated: (g.lines || []).some(l => isEstimatedLine(l)),
        }));
        this.expanded = this.groups.length ? [this.groups[0]] : [];
      } catch (e) {
        this.$store.dispatch("toast", { type: "error", message: "Gagal memuat rincian" });
      } finally {
        this.loading = false;
      }
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const res = await this.$store.dispatch("budgetMonitor/exportPo", this.context.crmPoId);
        fileDownload(res.data, `budget-${this.context.poNumber || this.context.crmPoId}.xlsx`);
      } catch (e) {
        this.$store.dispatch("toast", { type: "error", message: "Gagal mengekspor" });
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.clickable-cell {
  cursor: pointer;
  color: #1976d2;
  text-decoration: underline;
}
</style>
